import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./stylesAllSports.css";
import "../SportsRelated/CategoryRow/stylesCategoryRow.css";

import ClipPath from "../../../../assets/icons/ic_clip-path.svg";
import Planet from "../../../../assets/icons/ic_planet.svg";
import EditIcon from "../../../../assets/icons/ic_edit.svg";

import {
  DownOutlined,
  PushpinFilled,
  PushpinOutlined,
  UpOutlined,
  TrophyOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import Sortable from "sortablejs";
import { Select } from "antd";
import { message } from "antd";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { SPORT_LOGO_BASE_URL } from "../../../../utils/Constants";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import ToggleSwitch from "../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import { useUpdateSportsbookEntityName } from "../../../../queries/sportsbook-fm/useUpdateSportsbookEntityName";
import { useSportsbookFm } from "../SportsbookFmContext";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";
import { RenameEntityModal } from "../Translations/RenameEntityModal";
import {
  applyListEntityRename,
  applyListEntityTranslations,
  getListEntityBaseName,
  toListTranslationEditorData,
} from "../Translations/listTranslationData";

// The backend message reports only what failed; the trailing clause describes
// the frontend's own recovery, so it stays frontend-owned copy.
const withRevertNotice = (text) =>
  `${text} ${AppStrings.SPORTSBOOK_FM_REVERTING_CHANGE}`;

const withRetryNotice = (text) =>
  `${text} ${AppStrings.SPORTSBOOK_FM_PLEASE_TRY_AGAIN}`;

function AllSports() {
  const { canEdit } = useSportsbookFm();
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const allSportsTableBodyRef = useRef(null);
  const sportsSortableRef = useRef(null);
  const countryRefs = useRef({});
  const pinnedLeaguesRefs = useRef({});
  const leagueRefs = useRef({});

  const [allSports, setAllSports] = useState([]);
  const [, setApiIsRunning] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [openedSports, setOpenedSports] = useState([]);
  const [openedCountries, setOpenedCountries] = useState([]);
  const [pinnedLeagues, setPinnedLeagues] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const renameTranslationData = useMemo(
    () =>
      toListTranslationEditorData(
        renameTarget,
        renameTarget?.id,
        renameTarget?.name ?? ""
      ),
    [renameTarget]
  );
  const updateSportNameMutation = useUpdateSportsbookEntityName();
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;
  const renameInitialName = translationsEnabled
    ? getListEntityBaseName(renameTarget, renameTarget?.name ?? "")
    : (renameTarget?.name ?? "");

  const triggerAllSportsCacheUpdate = useCallback(async () => {
    // Fire-and-forget because the backend endpoint still returns incorrect statuses ahead of code freeze.
    try {
      return await PrivateAdminPanelInstance.post(API_ROUTES.UPDATE_ALL_SPORTS_S3);
    } catch (error) {
      console.error("[AllSports] Failed to update All Sports cache", error);
      return undefined;
    }
  }, [PrivateAdminPanelInstance]);

  const handleSaveChanges = useCallback(async () => {
    if (!hasUnsavedChanges) return;

    setIsSaving(true);
    try {
      const response = await triggerAllSportsCacheUpdate();
      setHasUnsavedChanges(false);
      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED),
      );
    } catch (error) {
      // Unreachable while triggerAllSportsCacheUpdate self-catches
      // (fire-and-forget); kept for when that wrapper is removed post-freeze.
      console.error("[AllSports] Failed to save changes", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsSaving(false);
    }
  }, [hasUnsavedChanges, triggerAllSportsCacheUpdate]);

  const saveSportName = async (name, translationChanges) => {
    if (!renameTarget) return;
    const previousSports = [...allSports];
    const toastKey = `sport-name-${renameTarget.id}`;

    try {
      const response = await updateSportNameMutation.mutateAsync({
        entityType: "ALL_SPORTS",
        sportId: renameTarget.id,
        displayName: name,
        ...(translationChanges
          ? {
              translationKey: translationChanges.translation_key,
              translations: translationChanges.translations,
            }
          : {}),
      });
      setAllSports((prev) =>
        prev.map((sport) => {
          if (sport.id !== renameTarget.id) return sport;
          const renamed = applyListEntityRename(
            sport,
            name,
            translationsEnabled,
            "name"
          );
          if (!translationChanges) return renamed;
          return applyListEntityTranslations(
            renamed,
            translationChanges.translations,
            "name",
            translationChanges.translation_key
          );
        })
      );
      setHasUnsavedChanges(true);
      message.open({
        key: toastKey,
        type: "success",
        content: getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
        ),
      });
    } catch (error) {
      console.error("[AllSports] Failed to update sport name", error);
      message.open({
        key: toastKey,
        type: "error",
        content: withRevertNotice(
          getApiErrorMessage(
            error,
            AppStrings.PAM_GENERIC_REQUEST_FAILED
          )
        ),
      });
      setAllSports(previousSports);
      return;
    }

    setRenameTarget(null);
  };

  const applySavedSportTranslations = ({ translation_key, translations }) => {
    if (!renameTarget) return;
    setAllSports((prev) =>
      prev.map((sport) =>
        sport.id === renameTarget.id
          ? applyListEntityTranslations(
              sport,
              translations,
              "name",
              translation_key
            )
          : sport
      )
    );
  };

  const reorderLeaguesWithPinnedFirst = useCallback(
    (leagues, prioritizedPinnedId = null) => {
      if (!Array.isArray(leagues)) return [];

      const pinned = [];
      const others = [];

      leagues.forEach((league) => {
        if (league?.is_pinned) {
          pinned.push(league);
        } else {
          others.push(league);
        }
      });

      const sortedPinned = pinned.slice().sort((a, b) => {
        if (prioritizedPinnedId) {
          if (a.id === prioritizedPinnedId) return -1;
          if (b.id === prioritizedPinnedId) return 1;
        }
        return (a.order ?? 0) - (b.order ?? 0);
      });

      const sortedOthers = others
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      return [...sortedPinned, ...sortedOthers].map((league, index) => ({
        ...league,
        order: index,
      }));
    },
    []
  );

  // Helper: resolve sport logo URL by slugifying sport name and applying overrides
  const getSportLogoUrl = (sportName) => {
    if (!sportName) return null;
    const overrides = {
      // normalized name (lowercase, no spaces/punctuation) : filename
      soccer: "soccer.png",
      football: "americanfootball.png",
      americanfootball: "americanfootball.png",
      basketball: "basketball.png",
      baseball: "baseball.png",
      tennis: "tennis.png",
      tabletennis: "tabletennis.png",
      icehockey: "icehockey.png",
      hockey: "icehockey.png",
      volleyball: "volleyball.png",
      handball: "handball.png",
      darts: "darts.png",
      mma: "mma.png",
      boxing: "boxing.png",
      cricket: "cricket.png",
      rugby: "rugby.png",
    };
    const normalized = String(sportName)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    const file = overrides[normalized] || `${normalized}.png`;
    return `${SPORT_LOGO_BASE_URL}/${file}`;
  };

  const updateSportStatus = (payload) => {
    const sportId = payload.event_code;
    const nextIsActive = payload.is_active;

    const previousState = [...allSports.map((s) => ({ ...s }))];

    const updated = allSports.map((s) =>
      s.id === sportId ? { ...s, status: nextIsActive } : s
    );

    setAllSports(updated);

    const toastKey = `sport-status-${sportId}`;
    const successFallback = AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED;

    const params = {
      entity_type: "ALL_SPORTS",
      item_type: "SPORT",
      entity_id: `SPORT#${sportId}`,
      is_active: nextIsActive,
    };

    (async () => {
      try {
        setApiIsRunning(true);
        const res = await PrivateAdminPanelInstance.patch(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          params
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        setHasUnsavedChanges(true);
        message.open({
          key: toastKey,
          type: "success",
          content: getApiSuccessMessage(res, successFallback),
        });
      } catch (err) {
        console.error("Error updating sport status", err);
        message.open({
          key: toastKey,
          type: "error",
          content: withRevertNotice(
            getApiErrorMessage(
              err,
              AppStrings.PAM_GENERIC_REQUEST_FAILED
            )
          ),
        });
        setAllSports(previousState);
      } finally {
        setApiIsRunning(false);
      }
    })();
  };

  const updateCountryStatus = (payload) => {
    const compositeId = payload.event_code; // "<sport_id>#<country_id>"
    const nextIsActive = payload.is_active;
    const [sportId] = compositeId.split("#");

    const previousState = [
      ...allSports.map((s) => ({
        ...s,
        countries:
          s.countries?.map((c) => ({
            ...c,
            leagues: c.leagues?.map((l) => ({ ...l })),
          })) || [],
      })),
    ];

    const updated = allSports.map((s) => {
      if (s.id !== sportId) return s;
      return {
        ...s,
        countries: (s.countries || []).map((c) =>
          c.id === compositeId
            ? {
                ...c,
                status: nextIsActive,
                leagues: (c.leagues || []).map((l) => ({
                  ...l,
                  status: nextIsActive,
                })),
              }
            : c
        ),
      };
    });

    setAllSports(updated);

    const toastKey = `country-status-${compositeId}`;
    const successFallback = AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED;

    const params = {
      entity_type: "ALL_SPORTS",
      item_type: "COUNTRY",
      entity_id: `COUNTRY#${compositeId}`,
      is_active: nextIsActive,
    };

    (async () => {
      try {
        setApiIsRunning(true);
        const res = await PrivateAdminPanelInstance.patch(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          params
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        setHasUnsavedChanges(true);
        message.open({
          key: toastKey,
          type: "success",
          content: getApiSuccessMessage(res, successFallback),
        });
      } catch (err) {
        console.error("Error updating country status", err);
        message.open({
          key: toastKey,
          type: "error",
          content: withRevertNotice(
            getApiErrorMessage(
              err,
              AppStrings.PAM_GENERIC_REQUEST_FAILED
            )
          ),
        });
        setAllSports(previousState);
      } finally {
        setApiIsRunning(false);
      }
    })();
  };

  const updateLeagueStatus = (payload) => {
    const leagueId = payload.event_code; // already like "LEAGUE#..."
    const nextIsActive = payload.is_active;

    const previousState = [
      ...allSports.map((s) => ({
        ...s,
        countries:
          s.countries?.map((c) => ({
            ...c,
            leagues: c.leagues?.map((l) => ({ ...l })),
          })) || [],
      })),
    ];

    const updated = allSports.map((s) => ({
      ...s,
      countries: (s.countries || []).map((c) => ({
        ...c,
        leagues: (c.leagues || []).map((l) =>
          l.id === leagueId ? { ...l, status: nextIsActive } : l
        ),
      })),
    }));

    setAllSports(updated);

    const toastKey = `league-status-${leagueId}`;
    const successFallback = AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED;

    const params = {
      entity_type: "ALL_SPORTS",
      item_type: "LEAGUE",
      entity_id: leagueId,
      is_active: nextIsActive,
    };

    (async () => {
      try {
        setApiIsRunning(true);
        const res = await PrivateAdminPanelInstance.patch(
          API_ROUTES.SET_ENTITY_ACTIVE_STATUS,
          params
        );
        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }
        setHasUnsavedChanges(true);
        message.open({
          key: toastKey,
          type: "success",
          content: getApiSuccessMessage(res, successFallback),
        });
      } catch (err) {
        console.error("Error updating league status", err);
        message.open({
          key: toastKey,
          type: "error",
          content: withRevertNotice(
            getApiErrorMessage(
              err,
              AppStrings.PAM_GENERIC_REQUEST_FAILED
            )
          ),
        });
        setAllSports(previousState);
      } finally {
        setApiIsRunning(false);
      }
    })();
  };

  const callPinLeague = async (entityType, league) => {
    const params = {
      entity_type: entityType,
      entity_id: league.id,
    };

    setApiIsRunning(true);
    try {
      const res = await PrivateAdminPanelInstance.post(
        API_ROUTES.PIN_ENTITY,
        params
      );
      if (res.status !== 200 || res.data?.success === false) {
        throw new Error(`Status ${res.status}`);
      }
      setHasUnsavedChanges(true);
      return res;
    } catch (err) {
      console.error("Error pinning league", err);
      throw err;
    } finally {
      setApiIsRunning(false);
    }
  };

  const callUnPinLeague = async (entityType, league) => {
    const params = {
      entity_type: entityType,
      entity_id: league.id,
    };

    setApiIsRunning(true);
    try {
      const res = await PrivateAdminPanelInstance.post(
        API_ROUTES.UNPIN_ENTITY,
        params
      );
      if (res.status !== 200 || res.data?.success === false) {
        throw new Error(`Status ${res.status}`);
      }
      setHasUnsavedChanges(true);
      return res;
    } catch (err) {
      console.error("Error unpinning league", err);
      throw err;
    } finally {
      setApiIsRunning(false);
    }
  };

  const pinLeague = async (sportId, league) => {
    const toastKey = `league-pin-${league.id}`;
    const previousPinned = pinnedLeagues.map((l) => ({ ...l }));
    const previousSports = allSports.map((sport) => ({
      ...sport,
      countries: (sport.countries || []).map((country) => ({
        ...country,
        leagues: (country.leagues || []).map((l) => ({ ...l })),
      })),
    }));

    const nextPinned = [
      {
        ...league,
        order: 0,
        sport_id: sportId,
        reorder_id: league.reorder_id || league.id?.replace("LEAGUE#", ""),
      },
      ...previousPinned.map((l, index) => ({
        ...l,
        order: index + 1,
      })),
    ];

    const nextSports = allSports.map((sport) => {
      if (sport.id !== sportId) return sport;
      return {
        ...sport,
        countries: (sport.countries || []).map((country) => {
          const hasLeague = (country.leagues || []).some(
            (l) => l.id === league.id
          );
          if (!hasLeague) return country;

          const updatedLeagues = (country.leagues || []).map((l) =>
            l.id === league.id ? { ...l, is_pinned: true } : l
          );

          return {
            ...country,
            leagues: reorderLeaguesWithPinnedFirst(updatedLeagues, league.id),
          };
        }),
      };
    });

    try {
      setApiIsRunning(true);
      setPinnedLeagues(nextPinned);
      setAllSports(nextSports);
      const response = await pinLeagueMutation.mutateAsync({ league });
      message.open({
        key: toastKey,
        type: "success",
        content: getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
        ),
      });
    } catch (err) {
      setPinnedLeagues(previousPinned.map((l) => ({ ...l })));
      setAllSports(previousSports);
      if (err?.response?.status === 404) {
        message.open({
          key: toastKey,
          type: "error",
          content: AppStrings.SPORTSBOOK_FM_LEAGUE_NOT_SEEDED,
        });
      } else {
        message.open({
          key: toastKey,
          type: "error",
          content: getApiErrorMessage(
            err,
            AppStrings.PAM_GENERIC_REQUEST_FAILED
          ),
        });
      }
      console.error("Error pinning league", err);
    } finally {
      setApiIsRunning(false);
    }
  };

  const unPinLeague = async (_sportId, league) => {
    const toastKey = `league-pin-${league.id}`;
    const previousPinned = pinnedLeagues.map((l) => ({ ...l }));
    const previousSports = allSports.map((sport) => ({
      ...sport,
      countries: (sport.countries || []).map((country) => ({
        ...country,
        leagues: (country.leagues || []).map((l) => ({ ...l })),
      })),
    }));

    const nextPinned = pinnedLeagues
      .filter((l) => l.id !== league.id)
      .map((l, index) => ({
        ...l,
        order: index,
      }));

    const nextSports = allSports.map((sport) => {
      if (sport.id !== _sportId) return sport;
      return {
        ...sport,
        countries: (sport.countries || []).map((country) => {
          const hasLeague = (country.leagues || []).some(
            (l) => l.id === league.id
          );
          if (!hasLeague) return country;

          const updatedLeagues = (country.leagues || []).map((l) =>
            l.id === league.id ? { ...l, is_pinned: false } : l
          );

          return {
            ...country,
            leagues: reorderLeaguesWithPinnedFirst(updatedLeagues),
          };
        }),
      };
    });

    try {
      setApiIsRunning(true);
      setPinnedLeagues(nextPinned);
      setAllSports(nextSports);
      const response = await unpinLeagueMutation.mutateAsync({ league });
      message.open({
        key: toastKey,
        type: "success",
        content: getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
        ),
      });
    } catch (err) {
      message.open({
        key: toastKey,
        type: "error",
        content: getApiErrorMessage(
          err,
          AppStrings.PAM_GENERIC_REQUEST_FAILED
        ),
      });
      setPinnedLeagues(previousPinned.map((l) => ({ ...l })));
      setAllSports(previousSports);
      console.error("Error unpinning league", err);
    } finally {
      setApiIsRunning(false);
    }
  };
  // No country pin/unpin actions

  // Stable API call for reordering entities (SPORT/COUNTRY/LEAGUE)
  const reorderEntities = useCallback(
    async (itemType = "SPORT", orderedIds, parentId = null) => {
      const params = {
        entity_type: "ALL_SPORTS",
        item_type: "SPORT",
        ordered_ids: orderedIds,
      };
      if (itemType === "COUNTRY" || itemType === "LEAGUE") {
        params.parent_id = parentId;
        params.item_type = itemType;
      }
      try {
        setApiIsRunning(true);

        const res = await PrivateAdminPanelInstance.post(
          API_ROUTES.REORDER_ALL_SPORTS_ENTITIES,
          params
        );

        if (res.status !== 200 || res.data?.success === false) {
          throw new Error(`Status ${res.status}`);
        }

        // Mark that we have unsaved changes
        setHasUnsavedChanges(true);

        return res;
      } catch (error) {
        console.error("error reordering entities", error);
        throw error;
      } finally {
        setApiIsRunning(false);
      }
    },
    [PrivateAdminPanelInstance]
  );

  const updateLeagueOrder = useCallback(
    async (sportId, countryId, oldIndex, newIndex) => {
      setAllSports((prevSports) =>
        prevSports.map((sport) => {
          if (sport.id !== sportId) return sport;
          return {
            ...sport,
            countries: sport.countries.map((country) => {
              if (country.id !== countryId) return country;
              const newLeagues = [...country.leagues];
              const [movedItem] = newLeagues.splice(oldIndex, 1);
              newLeagues.splice(newIndex, 0, movedItem);
              const reorderedLeagues = newLeagues.map((item, index) => ({
                ...item,
                order: index,
              }));

              const previousState = prevSports;
              const toastKey = `league-order-${countryId}`;
              (async () => {
                try {
                  const parentForReorder =
                    reorderedLeagues.find((league) => league.parent_country_id)
                      ?.parent_country_id ||
                    country.league_parent_reference ||
                    country.entity_id;
                  const response = await reorderEntities(
                    "LEAGUE",
                    reorderedLeagues.map((league) => league.reorder_id),
                    parentForReorder
                  );
                  message.open({
                    key: toastKey,
                    type: "success",
                    content: getApiSuccessMessage(
                      response,
                      AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
                    ),
                  });
                } catch (err) {
                  console.error("Error updating backend", err);
                  setAllSports(previousState);
                  message.open({
                    key: toastKey,
                    type: "error",
                    content: withRetryNotice(
                      getApiErrorMessage(
                        err,
                        AppStrings.PAM_GENERIC_REQUEST_FAILED
                      )
                    ),
                  });
                }
              })();

              return { ...country, leagues: reorderedLeagues };
            }),
          };
        })
      );
    },
    [setAllSports, reorderEntities]
  );

  const updateCountryOrder = useCallback(
    (sportId, oldIndex, newIndex) => {
      setAllSports((prevSports) =>
        prevSports.map((sport) => {
          if (sport.id !== sportId) return sport;

          const updatedCountries = [...sport.countries];
          const [movedCountry] = updatedCountries.splice(oldIndex, 1);
          updatedCountries.splice(newIndex, 0, movedCountry);

          const reordered = updatedCountries.map((c, i) => ({
            ...c,
            order: i,
          }));

          const previousState = prevSports;
          const toastKey = `country-order-${sportId}`;

          (async () => {
            try {
              const response = await reorderEntities(
                "COUNTRY",
                reordered.map((country) => country.id),
                sport.id
              );
              message.open({
                key: toastKey,
                type: "success",
                content: getApiSuccessMessage(
                  response,
                  AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
                ),
              });
            } catch (err) {
              console.error("Error updating backend", err);
              setAllSports(previousState);
              message.open({
                key: toastKey,
                type: "error",
                content: withRetryNotice(
                  getApiErrorMessage(
                    err,
                    AppStrings.PAM_GENERIC_REQUEST_FAILED
                  )
                ),
              });
            }
          })();

          return {
            ...sport,
            countries: reordered,
          };
        })
      );
    },
    [setAllSports, reorderEntities]
  );

  const pinLeagueMutation = useMutation({
    mutationFn: ({ league }) => callPinLeague("ALL_SPORTS", league),
  });

  const unpinLeagueMutation = useMutation({
    mutationFn: ({ league }) => callUnPinLeague("ALL_SPORTS", league),
  });

  // Countries are not pinnable in All Sports

  const getAllSports = async () => {
    try {
      setApiIsRunning(true);
      const requests = [
        PrivateAdminPanelInstance.post(API_ROUTES.GET_SPORTS_ORDERING, {
          entity_type: "ALL_SPORTS",
        }),
        PrivateAdminPanelInstance.post(
          API_ROUTES.GET_ALL_SPORTS_ENTITIES_BY_TYPE,
          {
            entity_type: "ALL_SPORTS",
            item_type: "COUNTRY",
          }
        ),
        PrivateAdminPanelInstance.post(
          API_ROUTES.GET_ALL_SPORTS_ENTITIES_BY_TYPE,
          {
            entity_type: "ALL_SPORTS",
            item_type: "LEAGUE",
          }
        ),
      ];

      const results = await Promise.allSettled(requests);
      const sportsRes = results[0];
      const countriesRes = results[1];
      const leaguesRes = results[2];
      const sports =
        sportsRes.status === "fulfilled"
          ? sportsRes.value.data?.data?.sports || []
          : [];
      const countries =
        countriesRes.status === "fulfilled"
          ? countriesRes.value.data?.data?.entities || []
          : [];
      const leagues =
        leaguesRes.status === "fulfilled"
          ? leaguesRes.value.data?.data?.entities || []
          : [];

      const formatted = sports.map((s) => ({
        id: s.sport_id,
        name: s.name,
        translations: s.translations,
        order: s.display_order,
        status: s.is_active,
        is_pinned: s.is_pinned,
        countries: countries
          .filter((c) => c.parent_sport_id === s.sport_id)
          .sort((a, b) => {
            const ao = a.display_order ?? 0;
            const bo = b.display_order ?? 0;
            return ao - bo;
          })
          .map((country, index) => {
            const countryIdComposite = `${country.parent_sport_id}#${country.country_id}`;
            const childLeagues = leagues
              .filter((l) => {
                const parentId = l.parent_country_id;
                const composite = `COUNTRY#${country.parent_sport_id}#${country.country_id}`;
                // Accept both composite and raw country_id forms for safety
                return (
                  parentId === composite ||
                  parentId === country.entity_id ||
                  parentId === country.country_id
                );
              })
              .sort((a, b) => {
                const ap = a.is_pinned ? 0 : 1;
                const bp = b.is_pinned ? 0 : 1;
                if (ap !== bp) return ap - bp;
                const ao = a.pinned_order ?? a.display_order ?? 0;
                const bo = b.pinned_order ?? b.display_order ?? 0;
                return ao - bo;
              })
              .map((l, idx) => ({
                id: l.entity_id,
                reorder_id: l.entity_id.replace("LEAGUE#", ""),
                name: l.name,
                order: idx,
                status: l.is_active,
                is_pinned: !!l.is_pinned,
                logo_url: l.league_logo_url || null,
                parent_country_id: l.parent_country_id || null,
              }));

            return {
              id: countryIdComposite,
              entity_id: `COUNTRY#${countryIdComposite}`,
              country_id: country.country_id,
              league_parent_reference:
                (childLeagues[0] && childLeagues[0].parent_country_id) ||
                country.entity_id ||
                country.country_id,
              name: country.country_name,
              order: index,
              status: country.is_active,
              flag_url: country.country_flag_url || null,
              leagues: childLeagues,
            };
          }),
      }));

      const pinned = leagues
        .filter((l) => l.is_pinned)
        .reduce(
          (acc, l) => {
            const key = `${l.sport_id}__${l.league_id}`;
            if (!acc.map.has(key)) {
              acc.map.set(key, true);
              acc.items.push({
                id: l.entity_id,
                reorder_id: l.entity_id.replace("LEAGUE#", ""),
                name: l.name,
                order: l.pinned_order ?? 0,
                status: l.is_active,
                sport_id: l.sport_id,
                logo_url: l.league_logo_url || null,
              });
            }
            return acc;
          },
          { map: new Map(), items: [] }
        )
        .items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      setAllSports(formatted);
      setPinnedLeagues(pinned);

      return formatted;
    } catch (e) {
      console.error("error getting all sports", e);
    } finally {
      setApiIsRunning(false);
      setDataLoaded(true);
    }
  };

  // React Query: fetch All Sports data
  useQuery({
    queryKey: ["all-sports", "sports-countries-leagues"],
    queryFn: getAllSports,
    refetchOnWindowFocus: false,
    onSuccess: () => {},
  });

  useEffect(() => {
    if (!allSportsTableBodyRef.current) return;
    // Clean existing instance (StrictMode double-invoke safe)
    if (sportsSortableRef.current) {
      sportsSortableRef.current.destroy();
      sportsSortableRef.current = null;
    }
    
    if (!canEdit) return;
    
    sportsSortableRef.current = Sortable.create(allSportsTableBodyRef.current, {
      animation: 150,
      onEnd: (evt) => {
        setAllSports((prevSports) => {
          if (!prevSports.length) return prevSports;
          const newOrder = [...prevSports];
          const [movedItem] = newOrder.splice(evt.oldIndex, 1);
          newOrder.splice(evt.newIndex, 0, movedItem);
          const updatedOrder = newOrder.map((item, idx) => ({
            ...item,
            order: idx,
          }));
          const previousState = prevSports;
          const toastKey = `sport-order-${movedItem?.id || "unknown"}`;
          (async () => {
            try {
              const response = await reorderEntities(
                "SPORT",
                updatedOrder.map((sport) => sport.id),
                null
              );
              message.open({
                key: toastKey,
                type: "success",
                content: getApiSuccessMessage(
                  response,
                  AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
                ),
              });
            } catch (err) {
              console.error("Error updating backend", err);
              setAllSports(previousState);
              message.open({
                key: toastKey,
                type: "error",
                content: withRetryNotice(
                  getApiErrorMessage(
                    err,
                    AppStrings.PAM_GENERIC_REQUEST_FAILED
                  )
                ),
              });
            }
          })();
          return updatedOrder;
        });
      },
    });
    return () => {
      if (sportsSortableRef.current) {
        sportsSortableRef.current.destroy();
        sportsSortableRef.current = null;
      }
    };
  }, [reorderEntities, canEdit]);

  useEffect(() => {
    if (!countryRefs.current || !canEdit) return;
    const sortables = [];
    Object.entries(countryRefs.current).forEach(([_countryId, el]) => {
      if (!el) return;
      const sortable = Sortable.create(el, {
        animation: 150,
        handle: ".drag-handle-country",
        onEnd: (evt) => {
          const sportId = evt.item.dataset.sportId;
          updateCountryOrder(sportId, evt.oldIndex, evt.newIndex);
        },
      });
      sortables.push(sortable);
    });
    return () => {
      sortables.forEach((s) => s.destroy());
    };
  }, [openedSports, updateCountryOrder, canEdit]);

  useEffect(() => {
    if (!leagueRefs.current || !canEdit) return;
    const sortables = [];
    Object.entries(leagueRefs.current).forEach(([_countryId, el]) => {
      if (!el) return;
      const sortable = Sortable.create(el, {
        animation: 150,
        handle: ".drag-handle-league",
        onEnd: (evt) => {
          const sportId = evt.item.dataset.sportId;
          const countryId = evt.item.dataset.countryId;
          updateLeagueOrder(sportId, countryId, evt.oldIndex, evt.newIndex);
        },
      });
      sortables.push(sortable);
    });
    return () => {
      sortables.forEach((s) => s.destroy());
    };
  }, [openedCountries, updateLeagueOrder, canEdit]);

  useEffect(() => {
    if (!pinnedLeaguesRefs.current || !canEdit) return;
    const sortables = [];
    Object.entries(pinnedLeaguesRefs.current).forEach(([sportId, el]) => {
      if (!el) return;
      const sortable = Sortable.create(el, {
        animation: 150,
        handle: ".drag-handle-pinned-leagues",
        onEnd: (evt) => {
          const oldIndex = evt.oldIndex;
          const newIndex = evt.newIndex;
          setPinnedLeagues((prev) => {
            const sportPinned = prev.filter((l) => l.sport_id === sportId);
            const other = prev.filter((l) => l.sport_id !== sportId);
            const arr = [...sportPinned];
            const [moved] = arr.splice(oldIndex, 1);
            arr.splice(newIndex, 0, moved);
            const reindexed = arr.map((l, idx) => ({ ...l, order: idx }));

            const previous = prev;
            const toastKey = `pinned-league-order-${sportId}`;
            (async () => {
              try {
                const response = await reorderEntities(
                  "LEAGUE",
                  reindexed.map(
                    (l) => l.reorder_id || l.id.replace("LEAGUE#", "")
                  ),
                  sportId
                );
                message.open({
                  key: toastKey,
                  type: "success",
                  content: getApiSuccessMessage(
                    response,
                    AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
                  ),
                });
              } catch (err) {
                console.error("Error updating pinned leagues order", err);
                setPinnedLeagues(previous);
                message.open({
                  key: toastKey,
                  type: "error",
                  content: withRetryNotice(
                    getApiErrorMessage(
                      err,
                      AppStrings.PAM_GENERIC_REQUEST_FAILED
                    )
                  ),
                });
              }
            })();

            return [...other, ...reindexed];
          });
        },
      });
      sortables.push(sortable);
    });
    return () => sortables.forEach((s) => s.destroy());
  }, [openedSports, pinnedLeagues, reorderEntities, canEdit]);

  useEffect(() => {
    // data load handled by react-query
  }, []);

  return (
    <div className="sorting-table-wrapper">
      <div className="all-sports-header">
        <div className="all-sports-title">
          <h2>{AppStrings.ALL_SPORTS}</h2>
        </div>
        <div className="all-sports-actions">
          <button
            className={`save-changes-button ${
              hasUnsavedChanges ? "has-changes" : ""
            }`}
            onClick={handleSaveChanges}
            disabled={!hasUnsavedChanges || isSaving || !canEdit}
          >
            {isSaving ? (
              <>
                <LoadingOutlined className="save-button-loader" />
              </>
            ) : (
              AppStrings.SAVE_CHANGES_BUTTON
            )}
          </button>
        </div>
      </div>
      <table className="sorting-table">
        <thead>
          <tr className="sorting-table-header-row">
            <th className="sorting-table-header-cell" style={{ width: "10%" }}>
              <div className="sorting-table-header-cell-content">
                <span># {AppStrings.ORDER}</span>
              </div>
            </th>
            <th className="sorting-table-header-cell" style={{ width: "20%" }}>
              <div className="sorting-table-header-cell-content">
                <span>{AppStrings.ICON}</span>
              </div>
            </th>
            <th className="sorting-table-header-cell" style={{ width: "40%" }}>
              <div className="sorting-table-header-cell-content">
                <span>{AppStrings.SPORT}</span>
              </div>
            </th>
            <th className="sorting-table-header-cell" style={{ width: "10%" }}>
              <div className="sorting-table-header-cell-content">
                <span>{AppStrings.STATUS}</span>
              </div>
            </th>
            <th className="sorting-table-header-cell" style={{ width: "10%" }}>
              <div className="sorting-table-header-cell-content">
                <span>{AppStrings.ACTIVITY}</span>
              </div>
            </th>
            <th className="sorting-table-header-cell" style={{ width: "10%" }}>
              <div className="sorting-table-header-cell-content"></div>
            </th>
          </tr>
        </thead>
        <tbody
          ref={allSportsTableBodyRef}
          className="fm-individual-sport-table-country-container"
        >
          {dataLoaded &&
            allSports.map((sport, index) => (
              <React.Fragment key={sport.id}>
                <tr className="category-row">
                  <td className="category-row__cell drag-handle">
                    <div className="category-row__cell-order">
                      <img src={ClipPath} alt="" />
                      {index + 1}
                    </div>
                  </td>
                  <td className="category-row__cell">
                    <div className="category-row__live-icon">
                      <TrophyOutlined className="fm-all-sports-table-sport-icon" />
                      <img
                        className="fm-all-sport-table-sport-icon-img"
                        src={getSportLogoUrl(sport.name)}
                        alt={formatString(
                          AppStrings.SPORTSBOOK_FM_ENTITY_LOGO_ALT,
                          { name: sport.name }
                        )}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  </td>
                  <td className="category-row__cell">
                    <div className="sport-name-cell">
                      <span className="table-row-text">{sport.name}</span>
                      {canEdit && (
                        <button
                          className="name-edit-btn"
                          title={AppStrings.EDIT_NAME}
                          onClick={() => setRenameTarget(sport)}
                        >
                          <img
                            src={EditIcon}
                            alt={AppStrings.EDIT}
                            className="edit-icon"
                          />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="category-row__cell">
                    <ToggleSwitch
                      status={!!sport.status}
                      eventCode={sport.id}
                      updateEventCode={updateSportStatus}
                      disabled={!canEdit}
                    />
                  </td>
                  <td className="category-row__cell"></td>
                  <td className="category-row__cell">
                    <button
                      onClick={() =>
                        setOpenedSports((s) =>
                          s.includes(sport.id)
                            ? s.filter((id) => id !== sport.id)
                            : [...s, sport.id]
                        )
                      }
                    >
                      {openedSports.includes(sport.id) ? (
                        <DownOutlined />
                      ) : (
                        <UpOutlined />
                      )}
                    </button>
                  </td>
                </tr>
                {openedSports.includes(sport.id) && (
                  <tr>
                    <td colSpan={6}>
                      <div className="fm-all-sport-table-pinned-leagues-container">
                        <div className="fm-all-sport-table-pinned-leagues-header">
                          <h3>
                            {formatString(
                              AppStrings.SPORTSBOOK_FM_PINNED_LEAGUES_HEADING,
                              { name: sport.name }
                            )}
                          </h3>
                        </div>
                        <div
                          className="fm-all-sport-table-pinned-leagues-list"
                          ref={(el) => {
                            if (el) pinnedLeaguesRefs.current[sport.id] = el;
                          }}
                        >
                          {pinnedLeagues
                            .filter((league) => league.sport_id === sport.id)
                            .map((league) => (
                              <div
                                className="fm-all-sport-table-pinned-leagues-list-item drag-handle-pinned-leagues"
                                key={league.id}
                                data-id={league.id}
                                data-order={league.order}
                              >
                                <img src={ClipPath} alt="ClipPath" />
                                <div className="fm-all-sport-table-pinned-leagues-list-item-text-container">
                                  <img
                                    className="fm-all-sport-table-sport-icon"
                                    src={
                                      league.logo_url ||
                                      league.league_logo_url ||
                                      Planet
                                    }
                                    alt={formatString(AppStrings.SPORTSBOOK_FM_ENTITY_LOGO_ALT, {
                                      name: league.name,
                                    })}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = Planet;
                                    }}
                                  />
                                  <span className="fm-all-sport-table-pinned-leagues-list-item-text">
                                    {league.name}
                                  </span>
                                </div>
                                <PushpinFilled
                                  style={{
                                    color: "#FD6F27",
                                    cursor: canEdit ? "pointer" : "not-allowed",
                                  }}
                                  aria-label={formatString(
                                    AppStrings.SPORTSBOOK_FM_UNPIN_LEAGUE_ARIA,
                                    { name: league.name }
                                  )}
                                  onClick={() => canEdit && unPinLeague(sport.id, league)}
                                />
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="fm-all-sport-table-countries-container">
                        {(!sport.countries || sport.countries.length === 0) && (
                          <div style={{ padding: "12px 16px", color: "#888" }}>
                            {AppStrings.SPORTSBOOK_FM_NO_SPORT_DATA}
                          </div>
                        )}
                        <table className="fm-individual-sport-table">
                          {/* Countries container: sortable across countries */}
                          <tbody
                            ref={(el) => {
                              if (el) countryRefs.current[sport.id] = el;
                            }}
                            className="fm-individual-sport-table-country-container"
                          >
                            {sport.countries.map((country) => (
                              <React.Fragment key={country.id}>
                                <tr
                                  className="category-row children-row"
                                  data-sport-id={sport.id}
                                  data-id={country.id}
                                  data-order={country.order}
                                  data-country-id={country.id}
                                >
                                  <td className="category-row__cell" style={{ width: "10%" }}></td>
                                  <td className="category-row__cell" style={{ width: "20%" }}>
                                    <div className="fm-individual-sport-table-pin-container">
                                      <div className="drag-handle drag-handle-country fm-individual-sport-table-order-container">
                                        <img src={ClipPath} alt="ClipPath" />
                                      </div>
                                      <div className="fm-individual-sport-table-sport-icon-container">
                                        <img
                                          className="fm-individual-sport-table-img"
                                          src={
                                            country.flag_url ||
                                            country.country_flag_url ||
                                            Planet
                                          }
                                          alt={formatString(
                                            AppStrings.SPORTSBOOK_FM_COUNTRY_FLAG_ALT,
                                            { name: country.name }
                                          )}
                                          onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = Planet;
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </td>
                                  <td className="category-row__cell" style={{ width: "40%" }}>
                                    {country.name}
                                  </td>
                                  <td className="category-row__cell" style={{ width: "10%" }}>
                                    <ToggleSwitch
                                      status={!!country.status}
                                      eventCode={`${sport.id}#${country.country_id}`}
                                      updateEventCode={updateCountryStatus}
                                      disabled={!canEdit}
                                    />
                                  </td>
                                  <td className="category-row__cell" style={{ width: "10%" }}>
                                    <Select
                                      className="fm-individual-sport-table-country-order-select"
                                      options={Array.from(
                                        { length: sport.countries.length },
                                        (_, i) => ({
                                          label: `${i + 1}`,
                                          value: i,
                                        })
                                      )}
                                      onChange={(value) =>
                                        // updateCountryOrder matches on the bare
                                        // sport id, same as the drag path.
                                        updateCountryOrder(
                                          sport.id,
                                          country.order,
                                          value,
                                        )
                                      }
                                      value={country.order}
                                      disabled={!canEdit}
                                    />
                                  </td>
                                  <td className="category-row__cell" style={{ width: "10%" }}>
                                    <button
                                      onClick={() =>
                                        setOpenedCountries((s) =>
                                          s.includes(country.id)
                                            ? s.filter(
                                                (id) => id !== country.id
                                              )
                                            : [...s, country.id]
                                        )
                                      }
                                    >
                                      {openedCountries.includes(country.id) ? (
                                        <DownOutlined />
                                      ) : (
                                        <UpOutlined />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                {openedCountries.includes(country.id) && (
                                  <tr>
                                    <td colSpan={6}>
                                      <table
                                        className="fm-individual-sport-table"
                                        style={{ width: "100%" }}
                                      >
                                        <tbody
                                          ref={(el) => {
                                            if (el)
                                              leagueRefs.current[country.id] =
                                                el;
                                          }}
                                          className="fm-individual-sport-table-league-container"
                                        >
                                          {country.leagues.map((league) => (
                                            <tr
                                              key={`${country.id}__${league.id}`}
                                              className="fm-individual-sport-table-league-row drag-handle-league children-row"
                                              data-sport-id={sport.id}
                                              data-id={league.id}
                                              data-order={league.order}
                                              data-country-id={country.id}
                                            >
                                              <td className="category-row__cell" style={{ width: "10%" }}></td>
                                              <td className="category-row__cell" style={{ width: "20%" }}>
                                                <div className="fm-individual-sport-table-pin-container">
                                                  <div className="drag-handle fm-individual-sport-table-order-container">
                                                    <img
                                                      src={ClipPath}
                                                      alt="ClipPath"
                                                    />
                                                  </div>
                                                  <div className="fm-individual-sport-table-sport-icon-container">
                                                    <img
                                                      className="fm-individual-sport-table-img"
                                                      src={
                                                        league.logo_url ||
                                                        league.league_logo_url ||
                                                        Planet
                                                      }
                                                      alt={formatString(AppStrings.SPORTSBOOK_FM_ENTITY_LOGO_ALT, {
                                      name: league.name,
                                    })}
                                                      onError={(e) => {
                                                        e.currentTarget.onerror =
                                                          null;
                                                        e.currentTarget.src =
                                                          Planet;
                                                      }}
                                                    />
                                                  </div>
                                                  {pinnedLeagues.find(
                                                    (l) => l.id === league.id
                                                  ) ? (
                                                    <PushpinFilled
                                                      className="fm-individual-sport-table-pin-icon pinned"
                                                      style={{ cursor: canEdit ? "pointer" : "not-allowed" }}
                                                      aria-label={formatString(
                                    AppStrings.SPORTSBOOK_FM_UNPIN_LEAGUE_ARIA,
                                    { name: league.name }
                                  )}
                                                      onClick={() =>
                                                        canEdit && unPinLeague(
                                                          sport.id,
                                                          league
                                                        )
                                                      }
                                                    />
                                                  ) : (
                                                    <PushpinOutlined
                                                      className="fm-individual-sport-table-pin-icon"
                                                      style={{ cursor: canEdit ? "pointer" : "not-allowed" }}
                                                      aria-label={formatString(
                                                        AppStrings.SPORTSBOOK_FM_PIN_LEAGUE_ARIA,
                                                        { name: league.name }
                                                      )}
                                                      onClick={() =>
                                                        canEdit && pinLeague(
                                                          sport.id,
                                                          league
                                                        )
                                                      }
                                                    />
                                                  )}
                                                </div>
                                              </td>
                                              <td className="category-row__cell" style={{ width: "40%" }}>
                                                {league.name}
                                              </td>
                                              <td className="category-row__cell" style={{ width: "10%" }}>
                                                <ToggleSwitch
                                                  status={!!league.status}
                                                  eventCode={league.id}
                                                  updateEventCode={
                                                    updateLeagueStatus
                                                  }
                                                  disabled={!canEdit}
                                                />
                                              </td>
                                              <td className="category-row__cell" style={{ width: "10%" }}>
                                                <Select
                                                  className="fm-individual-sport-table-league-order-select"
                                                  options={Array.from(
                                                    {
                                                      length:
                                                        country.leagues.length,
                                                    },
                                                    (_, i) => ({
                                                      label: `${i + 1}`,
                                                      value: i,
                                                    })
                                                  )}
                                                  onChange={(value) =>
                                                    updateLeagueOrder(
                                                      sport.id,
                                                      country.id,
                                                      league.order,
                                                      value
                                                    )
                                                  }
                                                  value={league.order}
                                                  disabled={!canEdit}
                                                />
                                              </td>
                                              <td className="category-row__cell" style={{ width: "10%" }}></td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
        </tbody>
      </table>
      <RenameEntityModal
        open={!!renameTarget}
        title={AppStrings.SPORTSBOOK_FM_RENAME_SPORT}
        nameLabel={AppStrings.SPORTSBOOK_FM_SPORT_NAME}
        initialName={renameInitialName}
        entityKey={renameTarget?.translations?.key ?? renameTarget?.id}
        initialTranslationData={renameTranslationData}
        translationsEnabled={translationsEnabled}
        bundleTranslationChanges={translationsEnabled}
        saving={updateSportNameMutation.isPending}
        onSave={saveSportName}
        onTranslationsSaved={applySavedSportTranslations}
        onClose={() => setRenameTarget(null)}
      />
    </div>
  );
}

export default AllSports;
